import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { Cancel, Close, Edit, History, Save } from '@mui/icons-material'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../generic/hooks/useDateFormatter'
import { systemValueLabel } from '../../../../../generic/systemValues/systemValueLabel'
import IncidenceDetailItem from './IncidenceDetailItem'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'
import IncidenceForm from './IncidenceForm'

const IncidenceHistoryDetailDialog = ({
  open,
  onClose,
  incidence,
  isEditing = false,
  onStartModify,
  onCancelModify,
  onSaveModify,
  saving = false,
  error,
  comment = '',
  onCommentChange
}) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const { formatDate, formatTime } = useDateFormatter()

  const viewModel = useMemo(() => {
    if (!incidence) return null

    const start = incidence?.startDate
    const end = incidence?.endDate || incidence?.closeDate

    return {
      id: incidence?.id ?? '-',
      incidenceCode: incidence?.incidence?.code ?? '-',
      incidenceName: incidence?.incidence?.name ?? '-',
      incidenceType: incidence?.incidence?.incidenceType?.name ?? '-',
      incidenceStatus: systemValueLabel(t_, 'status', incidence?.incidence?.status) || incidence?.status || '-',
      device: incidence?.device ?? '-',
      duration: incidence?.duration ?? '-',
      startDate: start ? formatDate(start) : '-',
      startTime: start ? formatTime(start) : '-',
      endDate: end ? formatDate(end) : '-',
      endTime: end ? formatTime(end) : '-',
      createdBy: incidence?.createdBy?.code ?? '-',
      finishedBy: incidence?.finishedBy?.code ?? '-',
      comment: String(incidence?.comment ?? '').trim() || '-'
    }
  }, [formatDate, formatTime, incidence, t_])

  if (!open && !viewModel) return null

  const selectedTypeName = viewModel?.incidenceType ?? '-'
  const selectedIncidenceName = viewModel?.incidenceName ?? '-'

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ pr: 5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`
            }}
          >
            <History color='primary' />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant='h6' sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {viewModel?.incidenceName}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              {viewModel?.device}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label={t_('Close')}
          sx={{ position: 'absolute', right: 12, top: 10 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2.5, bgcolor: 'background.default' }}>
        {isEditing
          ? (
            <IncidenceForm
              loading={false}
              saving={saving}
              error={error}
              incidenceTypeOptions={selectedTypeName ? [selectedTypeName] : []}
              selectedTypeName={selectedTypeName}
              onTypeChange={() => {}}
              incidenceOptions={selectedIncidenceName ? [selectedIncidenceName] : []}
              selectedIncidenceName={selectedIncidenceName}
              onIncidenceChange={() => {}}
              comment={comment}
              onCommentChange={onCommentChange}
              disableType
              disableIncidence
            />
            )
          : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Type')} value={viewModel?.incidenceType} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Status')} value={viewModel?.incidenceStatus} />
              </Grid>

              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Start')} value={`${viewModel?.startDate} ${viewModel?.startTime}`} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('End')} value={`${viewModel?.endDate} ${viewModel?.endTime}`} />
              </Grid>

              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Duration')} value={viewModel?.duration} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Created By')} value={viewModel?.createdBy} />
              </Grid>

              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Finished By')} value={viewModel?.finishedBy} />
              </Grid>
              <Grid item xs={12} md={6}>
                <IncidenceDetailItem title={t_('Device')} value={viewModel?.device} />
              </Grid>

              <Grid item xs={12}>
                <IncidenceDetailItem title={t_('Comments')} value={viewModel?.comment} />
              </Grid>
            </Grid>
            )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        {isEditing
          ? (
            <>
              <Button
                variant='outlined'
                color='inherit'
                startIcon={<Cancel />}
                onClick={onCancelModify}
                disabled={saving}
              >
                {t_('CANCEL')}
              </Button>
              <WorkerRequiredButton
                variant='contained'
                color='primary'
                startIcon={<Save />}
                onClick={onSaveModify}
                disabled={saving}
              >
                {saving ? t_('SAVING...') : t_('SAVE CHANGES')}
              </WorkerRequiredButton>
            </>
            )
          : (
            <WorkerRequiredButton
              variant='outlined'
              startIcon={<Edit />}
              onClick={onStartModify}
            >
              {t_('MODIFY')}
            </WorkerRequiredButton>
            )}
      </DialogActions>
    </Dialog>
  )
}

IncidenceHistoryDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  incidence: PropTypes.object,
  isEditing: PropTypes.bool,
  onStartModify: PropTypes.func,
  onCancelModify: PropTypes.func,
  onSaveModify: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.any,
  comment: PropTypes.string,
  onCommentChange: PropTypes.func
}

export default IncidenceHistoryDetailDialog
